import { EventPart, PublicEventLocale } from '@hopin-team/api-hopin';
import { TFunction } from '@hopin-team/provider-translation';
import { Button } from '@hopin-team/ui-button';
import { Flex } from '@hopin-team/ui-flex';
import { ColorProp } from '@hopin-team/ui-helper-get-symbol';
import { Icon, IconProp } from '@hopin-team/ui-icon';
import { Menu } from '@hopin-team/ui-menu';
import { SizeProp } from '@hopin-team/ui-tag/dist/styled/root';
import { Text } from '@hopin-team/ui-text';
import { TextField } from '@hopin-team/ui-text-field';
import React, {
  SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  eventPartIcons,
  getEventPartLabel,
  ScheduleTabsContext,
  TimeStatus,
  truncateString,
} from '../..';
import { useLocales } from '../../hooks';
import { PartSchedule } from '../../types';
import { CheckOptions } from './CheckOptions';
import {
  Butticon,
  MenuContent,
  SearchContainer,
  SearchFieldButticon,
} from './styles/scheduletabs-styles';

const handleCheck = (
  values: SyntheticEvent<HTMLInputElement>,
  currentList: string[],
  setter: (values: any[]) => void,
) => {
  if (currentList.includes(values.currentTarget.value)) {
    setter(currentList.filter(value => value !== values.currentTarget.value));
  } else {
    setter([...currentList, values.currentTarget.value]);
  }
};

const renderSegmentLabel =
  (
    t: TFunction,
    eventLocale?: PublicEventLocale,
    customEventPartTitle?: string,
    appTitle?: string,
  ) =>
  (label?: string | null) =>
    truncateString(
      getEventPartLabel({
        t,
        eventPart: label as EventPart,
        eventLocale,
        customEventPartTitle,
        appTitle,
      }) || '',
      24,
      '...',
    );

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};
const LARGE_WIDTH = 860;

export const Filters = ({
  schedule,
  eventLocale,
  isVirtualEvent = true,
  containerWidth = 0,
  size = 'small',
}: {
  schedule?: PartSchedule;
  eventLocale?: PublicEventLocale;
  isVirtualEvent?: boolean;
  containerWidth?: number;
  size?: 'small' | 'medium';
}) => {
  const {
    setSelectedTags,
    setSelectedParts,
    setSelectedEventTypes,
    setSearchValue,
    selectedTags,
    selectedParts,
    selectedEventTypes,
    searchValue,
    selectedTimeStatuses,
    setSelectedTimeStatuses,
    selectedEditingStatuses,
    setSelectedEditingStatuses,
    scheduleContext,
  } = useContext(ScheduleTabsContext);

  const isInEvent = scheduleContext === 'in-event';
  const isOrganiser = scheduleContext === 'organiser';

  const butticonSignal = isInEvent;
  const butticonOutlined = isInEvent;
  const butticonMargin = isInEvent ? 0 : 2;
  let butticonSize: SizeProp;
  if (isInEvent) {
    butticonSize = 'medium';
  } else if (scheduleContext === 'registration') {
    butticonSize = 'tiny';
  } else {
    butticonSize = 'small';
  }
  const enabledFilterSections = {
    timeStatus: isInEvent,
    search: !isInEvent,
    eventFormat: true,
    part: true,
    tags: !isInEvent,
    draft: isOrganiser,
  };

  const items = useMemo(() => schedule?.items || [], [schedule?.items]);
  const organiser = schedule?.organiser;
  const { t } = useLocales('event');
  const [menuOpen, setMenuOpen] = useState(false);
  const isLargeContainer = containerWidth > LARGE_WIDTH;
  const isFullsizeButton = size === 'medium';

  const timeStatusOptions = useMemo(
    () =>
      ['past', 'live', 'future']
        .filter(timeStatus =>
          items.map(item => item.timeStatus).includes(timeStatus as TimeStatus),
        )
        .map(status => ({ label: status })),
    [items],
  );

  useEffect(() => {
    if (timeStatusOptions.map(opt => opt.label).join('') === 'past') {
      setSelectedTimeStatuses(['past']);
    }
  }, [timeStatusOptions, setSelectedTimeStatuses]);

  const tagOptions = useMemo(
    () =>
      Array.from(
        new Set((items || []).flatMap(item => item.tags.map(tag => tag.name))),
      )
        .sort()
        .map(tag => ({ label: tag })),
    [items],
  );

  const partOptions = useMemo(
    () =>
      Array.from(
        new Set((items || []).flatMap(item => item.event_part_type)),
      ).map(part => ({
        label: part,
        ...(scheduleContext === 'in-event'
          ? {
              icon: (
                <Icon
                  mr={1}
                  mt={0.5}
                  name={eventPartIcons[part].icon as IconProp}
                  color={
                    eventPartIcons[part].textColor
                      .replace('COLOR.', '')
                      .replace(/\./g, '-')
                      .toLowerCase() as ColorProp
                  }
                />
              ),
            }
          : {}),
      })),
    [items, scheduleContext],
  );

  const eventTypeOptions = useMemo(
    () =>
      Array.from(new Set((items || []).flatMap(item => item.event_type))).map(
        type => ({ label: type }),
      ),
    [items],
  );

  const draftOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (items || []).flatMap(item => (item.draft ? 'draft' : 'final')),
        ),
      ).map(status => ({ label: status })),
    [items],
  );

  const filtersPanelEmpty =
    [...tagOptions, ...partOptions, ...eventTypeOptions].length === 0;

  const selectedFilters = [
    ...(enabledFilterSections.eventFormat ? selectedEventTypes : []),
    ...(enabledFilterSections.part ? selectedParts : []),
    ...(enabledFilterSections.tags ? selectedTags : []),
    ...(enabledFilterSections.timeStatus
      ? selectedTimeStatuses.filter(status => status === 'past')
      : []),
  ];

  const withSignal = butticonSignal && selectedFilters.length > 0;

  const search = (
    <SearchContainer ml={isLargeContainer ? 2 : 0}>
      <TextField
        type="search"
        name="search"
        hideLabel
        label=""
        placeholder={t('search')}
        value={searchValue}
        onSubmit={noop}
        onClear={() => setSearchValue('')}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearchValue(e.currentTarget.value)
        }
        size={isFullsizeButton ? 'medium' : 'small'}
        butticon={
          <SearchFieldButticon
            pattern="secondary"
            icon="search"
            title={t('search')}
            size={isFullsizeButton ? 'small' : 'tiny'}
            type="button"
            onClick={noop}
          />
        }
      />
    </SearchContainer>
  );

  const filterButton = isFullsizeButton ? (
    <Button
      pattern="secondary"
      id="options"
      data-testid="options"
      leadingIcon="filter"
      size="medium"
      isInline
      isOutlined
      onClick={() => setMenuOpen(!menuOpen)}
      px={2}
      ml={2}
    >
      {t('filter')}
    </Button>
  ) : (
    <Butticon
      id="options"
      data-testid="options"
      icon="filter"
      title={t('filter')}
      onClick={() => setMenuOpen(!menuOpen)}
      ml={butticonMargin}
      size={butticonSize}
      isOutlined={butticonOutlined}
      withSignal={withSignal}
    />
  );

  return (
    <Flex
      alignItems="center"
      justifyContent="flex-end"
      mr={scheduleContext === 'in-event' ? 0 : 1}
    >
      {isLargeContainer && enabledFilterSections.search && search}
      {!menuOpen && filterButton}
      <Menu
        onClose={() => setMenuOpen(false)}
        isDismissible
        isInline
        isShowing={menuOpen}
      >
        {menuOpen && filterButton}
        <MenuContent p={1} pb={0} size="small">
          {!isLargeContainer && enabledFilterSections.search && search}
          {filtersPanelEmpty ? (
            <Flex m={2}>
              <Text pattern="emphasis">{t('no-filters')}</Text>
            </Flex>
          ) : (
            <>
              {enabledFilterSections.timeStatus && (
                <CheckOptions
                  options={timeStatusOptions}
                  onChange={values =>
                    handleCheck(
                      values,
                      selectedTimeStatuses,
                      setSelectedTimeStatuses,
                    )
                  }
                  checkedOptions={selectedTimeStatuses}
                  name={t('status')}
                  labelRenderer={timeStatus =>
                    t(timeStatus === 'future' ? 'upcoming' : timeStatus)
                  }
                />
              )}

              {enabledFilterSections.part && (
                <CheckOptions
                  options={partOptions}
                  onChange={values =>
                    handleCheck(values, selectedParts, setSelectedParts)
                  }
                  checkedOptions={selectedParts}
                  name={t('segments')}
                  labelRenderer={renderSegmentLabel(
                    t,
                    eventLocale,
                    organiser?.custom_event_part_title,
                    organiser?.app_title,
                  )}
                />
              )}

              {enabledFilterSections.tags && (
                <CheckOptions
                  options={tagOptions}
                  onChange={values =>
                    handleCheck(values, selectedTags, setSelectedTags)
                  }
                  checkedOptions={selectedTags}
                  name={t('tags')}
                />
              )}

              {!isVirtualEvent && enabledFilterSections.eventFormat && (
                <CheckOptions
                  options={eventTypeOptions}
                  onChange={values =>
                    handleCheck(
                      values,
                      selectedEventTypes,
                      setSelectedEventTypes,
                    )
                  }
                  checkedOptions={selectedEventTypes}
                  name={t('eventType')}
                  labelRenderer={t}
                />
              )}

              {enabledFilterSections.draft && (
                <CheckOptions
                  options={draftOptions}
                  onChange={values =>
                    handleCheck(
                      values,
                      selectedEditingStatuses,
                      setSelectedEditingStatuses,
                    )
                  }
                  checkedOptions={selectedEditingStatuses}
                  name={t('editingStatus')}
                  labelRenderer={t}
                />
              )}
            </>
          )}
        </MenuContent>
      </Menu>
    </Flex>
  );
};
